import React, { useEffect, useState, useMemo, Suspense, lazy } from "react";
import { getLocations } from "../utils/locationsData";
import { LocationsList } from "../components/locations/LocationsList";
import { getCountryColorMap } from "../utils/countryColors";
import "./LocationsPage.css";

// Lazy-loaded so /portfolio and other routes don't pay for the topojson bundle.
const LocationsMap = lazy(() =>
  import("../components/locations/LocationsMap").then((m) => ({ default: m.LocationsMap }))
);

const LocationsHubPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCode, setHoveredCode] = useState(null);
  const [focusedCode, setFocusedCode] = useState(null);

  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await getLocations();
        setLocations(rows);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  // List <-> map hover/focus sync (locations-browser.md §4 interaction rule 1).
  const activeCode = useMemo(() => hoveredCode || focusedCode, [hoveredCode, focusedCode]);
  const colorMap = useMemo(
    () => getCountryColorMap(locations.map((loc) => loc.countryCode)),
    [locations]
  );

  return (
    <section className="locations-hub">
      <h1 className="locations-hub-title">Locations — Around the world</h1>

      {loading && locations.length === 0 && (
        <p className="locations-loading">Loading locations...</p>
      )}
      {error && locations.length === 0 && (
        <p className="locations-error">Failed to load locations. Please try again later.</p>
      )}
      {!loading && !error && locations.length === 0 && (
        <p className="locations-empty">No locations with photos yet.</p>
      )}

      {locations.length > 0 && (
        <div className="locations-hub-layout">
          <Suspense fallback={<div className="locations-map-loading" aria-hidden="true" />}>
            <LocationsMap
              locations={locations}
              activeCode={activeCode}
              colorMap={colorMap}
              onHoverCode={setHoveredCode}
              onFocusCode={setFocusedCode}
            />
          </Suspense>

          <LocationsList
            locations={locations}
            activeCode={activeCode}
            colorMap={colorMap}
            onHoverCode={setHoveredCode}
            onFocusCode={setFocusedCode}
          />
        </div>
      )}
    </section>
  );
};

export default LocationsHubPage;
